import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebounce } from 'use-debounce';

import { AccountListRow } from '@/components/account/AccountListRow';
import { Input, InputField } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import {
  MIN_IMPERSONATION_QUERY_LENGTH,
  searchImpersonationTargetsV2QueryOptions,
} from '@/services/getImpersonationTargetsV2';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search for any account to act as, for staff whose impersonation scope is
 * `all`. Matches name or email; selecting a row hands its id to `onSelect`.
 */
export function ImpersonationSearch({ onSelect }: { onSelect: (userId: string) => void }) {
  const { colors } = useColors();

  const [query, setQuery] = useState('');

  const [debouncedQuery] = useDebounce(query.trim(), SEARCH_DEBOUNCE_MS);

  const { data, isFetching } = useQuery(
    searchImpersonationTargetsV2QueryOptions(debouncedQuery, true)
  );

  const isSearchable = debouncedQuery.length >= MIN_IMPERSONATION_QUERY_LENGTH;

  const results = isSearchable ? (data?.users ?? []) : [];

  return (
    <VStack space="xs" className="w-full">
      <Input>
        <InputField
          placeholder="Search any account by name or email"
          placeholderTextColor={colors.typography[600]}
          style={{ color: colors.typography[900] }}
          value={query}
          onChangeText={setQuery}
          autoCapitalize="none"
          autoCorrect={false}
          aria-label="Search accounts to act as"
        />
      </Input>

      {isSearchable && isFetching && results.length === 0 ? <Spinner size="small" /> : null}

      {isSearchable && !isFetching && results.length === 0 ? (
        <Text size="sm" className="text-typography-600">
          No accounts match “{debouncedQuery}”.
        </Text>
      ) : null}

      {results.map((user) => {
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

        return (
          <AccountListRow
            key={user.id}
            title={name || user.email || `Account ${user.id}`}
            subtitle={[user.userType, user.email].filter(Boolean).join(' · ')}
            onPress={() => {
              setQuery('');

              onSelect(String(user.id));
            }}
          />
        );
      })}
    </VStack>
  );
}
